import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import classNames from "classnames";
import { useUser } from "../../hooks/useUser";
import { useConfig } from "../../providers/useConfig";
import { useTranslations } from "../../providers/useTranslations";
import { replaceStringPlaceholder } from "../../utils/replaceStringPlaceholder/replaceStringPlaceholder";
import { AlertBanner } from "../AlertBanner";
import { ConsentCheckbox, ContactForm } from "../ContactForm";
import { Loading } from "../Loading";
import { Typography } from "../Typography";
import { useContestsBackendConfig } from "./hooks/useContestsBackendConfig";
import { LoyaltyContestFormProvider } from "./components";
import styles from "./LoyaltyContactForm.module.scss";
const getFilePickerPropsFromConfig = (config) => {
    return {
        acceptedFileTypes: config.file_mime_types,
        maxFiles: config.files_limit,
        maxSize: config.file_size_limit,
    };
};
export const LoyaltyContactForm = ({ heading, subHeading, imageSrc, isInEditor, successHeading, successSubHeading, successFormHeading, successFormSubHeading, theme = "light", consent = "contact_form", children, ...contactFormProps }) => {
    const { __ } = useTranslations();
    const { isUserLoggedIn, user } = useUser();
    const { isLoyaltyContestsServiceEnabled } = useConfig();
    const { isContestsBackendConfigError, isContestsBackendConfigLoading, contestsBackedConfig } = useContestsBackendConfig({
        ciam_token: user?.ciam_token,
        uuid: user?.uuid,
        isInEditor,
    });
    const [isSuccess, setIsSuccess] = useState(false);
    const hasReachedParticipationLimit = contestsBackedConfig &&
        contestsBackedConfig.month_participation_count >= contestsBackedConfig.month_participation_limit &&
        !isInEditor;
    if (!isUserLoggedIn && !isInEditor)
        return null;
    if (isContestsBackendConfigLoading)
        return _jsx(Loading, {});
    if (isContestsBackendConfigError) {
        console.error("Error fetching contests backend config:", isContestsBackendConfigError);
        return _jsx(AlertBanner, { variant: "error", title: __("loyalty.contact_form.error.title"), withIconClose: false });
    }
    const [title, description, formHeading, formSubHeading] = isSuccess
        ? [successHeading, successSubHeading, successFormHeading, successFormSubHeading]
        : [contactFormProps.title, contactFormProps.description, heading, subHeading];
    const hiddenFields = [
        { key: "First name", value: user?.first_name },
        { key: "Last name", value: user?.last_name },
        { key: "Email", value: user?.email },
    ];
    const contactFormTitle = isInEditor
        ? title
        : replaceStringPlaceholder(title, [{ placeholder: "Name", value: user?.first_name }], false);
    const loyaltyFormContent = hasReachedParticipationLimit && !isInEditor ? (_jsx("div", { className: styles.alertBanner, children: _jsx(AlertBanner, { variant: "info", title: __("loyalty.contact_form.already_entered.title"), description: __("loyalty.contact_form.already_entered.description"), withIconClose: false }) })) : (_jsx(LoyaltyContestFormProvider, { successScreenTitle: contactFormProps.successScreenTitle, title: contactFormTitle, description: description, onSuccess: () => setIsSuccess(true), sendButtonLabel: contactFormProps.sendButtonLabel, consent: consent, ...(!isInEditor &&
            contestsBackedConfig && {
            filePickerProps: getFilePickerPropsFromConfig(contestsBackedConfig),
            isFilePickerVisible: contestsBackedConfig?.file_upload_enabled,
        }), children: children }));
    const columnClasses = classNames(styles.column, {
        [styles.columnSingle]: hasReachedParticipationLimit,
    });
    return (_jsxs("div", { className: classNames(styles.wrapper, theme === "light" ? "light-theme" : "dark-theme"), children: [_jsxs("div", { className: styles.headingInfo, children: [_jsx(Typography, { className: styles.heading, weight: "light", variant: "h2", children: formHeading }), _jsx(Typography, { className: styles.subHeading, weight: "light", variant: "body1", children: formSubHeading })] }), _jsxs("div", { className: styles.formContent, children: [_jsx("div", { className: columnClasses, children: isLoyaltyContestsServiceEnabled ? (loyaltyFormContent) : (_jsx(ContactForm, { ...contactFormProps, onSuccess: () => setIsSuccess(true), title: contactFormTitle, description: description, className: styles.form, hiddenFields: hiddenFields, children: _jsx(ConsentCheckbox, { consent: consent, displayConsent: "always" }) })) }), !hasReachedParticipationLimit && (_jsx("div", { "data-testid": "loyalty-contact-form-img", className: classNames(styles.column, styles.image), style: { backgroundImage: `url(${imageSrc})` } }))] })] }));
};
//# sourceMappingURL=LoyaltyContactForm.js.map